import { useState, useEffect, useRef, useCallback } from "react";
import "./NotebookChat.css";

const API_BASE = "/api/notebook-chat";

function BotIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 8V4H8" />
      <rect width="16" height="12" x="4" y="8" rx="2" />
      <path d="M2 14h2" />
      <path d="M20 14h2" />
      <path d="M15 13v2" />
      <path d="M9 13v2" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg className="nc-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M21 12a9 9 0 1 1-6.22-8.56" />
    </svg>
  );
}

function SourceCitation({ sources }) {
  if (!sources?.length) return null;
  return (
    <div className="nc-sources">
      <span className="nc-sources-label">Sources</span>
      {sources.map((s, i) => (
        <span key={i} className="nc-source-chip">{s}</span>
      ))}
    </div>
  );
}

function Message({ msg }) {
  const isBot = msg.role === "assistant";
  return (
    <div className={`nc-message nc-message--${msg.role}`}>
      <div className="nc-message-avatar">
        {isBot ? <BotIcon /> : <UserIcon />}
      </div>
      <div className="nc-message-body">
        {msg.loading ? (
          <div className="nc-message-loading">
            <SpinnerIcon />
            <span>Asking NotebookLM…</span>
          </div>
        ) : (
          <>
            <p className="nc-message-text">{msg.content}</p>
            {isBot && <SourceCitation sources={msg.sources} />}
          </>
        )}
      </div>
    </div>
  );
}

export default function NotebookChat() {
  const [notebooks, setNotebooks] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [serviceStatus, setServiceStatus] = useState("loading"); // "loading" | "ok" | "unconfigured"
  const [conversationId, setConversationId] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Load notebooks on mount
  useEffect(() => {
    let cancelled = false;
    fetch(API_BASE)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setNotebooks(data);
          setSelectedId(data[0].id);
          setServiceStatus("ok");
        } else {
          setServiceStatus("unconfigured");
        }
      })
      .catch(() => {
        if (!cancelled) setServiceStatus("unconfigured");
      });
    return () => { cancelled = true; };
  }, []);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const askQuestion = useCallback(async (question) => {
    if (!question.trim() || busy || !selectedId) return;

    const userMsg = { id: Date.now(), role: "user", content: question };
    const loadingMsg = { id: Date.now() + 1, role: "assistant", loading: true, content: "" };

    setMessages((prev) => [...prev, userMsg, loadingMsg]);
    setInput("");
    setBusy(true);
    setError(null);

    try {
      const res = await fetch(API_BASE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notebook_id: selectedId, question, conversation_id: conversationId }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || data.detail || `HTTP ${res.status}`);
      }

      if (data.conversation_id) setConversationId(data.conversation_id);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === loadingMsg.id
            ? { ...m, loading: false, content: data.answer, sources: data.sources }
            : m
        )
      );
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== loadingMsg.id));
      setError(err.message);
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [busy, selectedId]);

  function handleSubmit(e) {
    e.preventDefault();
    askQuestion(input);
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      askQuestion(input);
    }
  }

  const selectedNotebook = notebooks.find((n) => n.id === selectedId);

  // ── Unconfigured state ───────────────────────────────────────────────
  if (serviceStatus === "unconfigured") {
    return (
      <div className="nc-page">
        <div className="nc-unconfigured">
          <div className="nc-unconfigured-icon">
            <BotIcon />
          </div>
          <h2>NotebookLM not connected</h2>
          <p>
            Deploy the Python microservice (<code>notebook-service/</code>) and
            set <code>NOTEBOOK_SERVICE_URL</code> in Cloudflare Pages environment
            variables to enable this feature.
          </p>
          <a
            href="https://github.com"
            className="nc-setup-link"
            target="_blank"
            rel="noreferrer"
          >
            Setup guide →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="nc-page">
      {/* Header */}
      <div className="nc-header">
        <div className="nc-header-left">
          <div className="nc-header-icon"><BotIcon /></div>
          <div>
            <h1 className="nc-title">Notebook AI</h1>
            <p className="nc-subtitle">
              Ask questions across your NotebookLM sources
            </p>
          </div>
        </div>

        {notebooks.length > 1 && (
          <div className="nc-notebook-picker">
            <label className="nc-picker-label" htmlFor="nc-notebook-select">
              Notebook
            </label>
            <select
              id="nc-notebook-select"
              className="nc-picker-select"
              value={selectedId}
              onChange={(e) => { setSelectedId(e.target.value); setConversationId(null); setMessages([]); }}
            >
              {notebooks.map((nb) => (
                <option key={nb.id} value={nb.id}>{nb.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Active notebook pill */}
      {selectedNotebook && notebooks.length === 1 && (
        <div className="nc-active-notebook">
          <span className="nc-active-dot" />
          <span>{selectedNotebook.label}</span>
        </div>
      )}

      {/* Message thread */}
      <div className="nc-thread" role="log" aria-live="polite" aria-label="Chat messages">
        {messages.length === 0 ? (
          <div className="nc-empty">
            <div className="nc-empty-graphic">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 8V4H8" />
                <rect width="16" height="12" x="4" y="8" rx="2" />
                <path d="M2 14h2" /><path d="M20 14h2" />
                <path d="M15 13v2" /><path d="M9 13v2" />
              </svg>
            </div>
            <h3>Ask your notebooks anything</h3>
            <p>Questions are sent directly to your NotebookLM sources.</p>

            <div className="nc-suggestions">
              {[
                "Summarize sensor trends in Room F9 this week",
                "What calibration issues have been flagged recently?",
                "Which rooms have the most unresolved alarms?",
              ].map((q) => (
                <button
                  key={q}
                  className="nc-suggestion-chip"
                  onClick={() => askQuestion(q)}
                  disabled={busy || !selectedId}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg) => <Message key={msg.id} msg={msg} />)
        )}
        <div ref={bottomRef} />
      </div>

      {/* Error banner */}
      {error && (
        <div className="nc-error" role="alert">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
          <button className="nc-error-dismiss" onClick={() => setError(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      {/* Input bar */}
      <form className="nc-input-bar" onSubmit={handleSubmit}>
        <textarea
          ref={inputRef}
          className="nc-input"
          rows={1}
          placeholder={selectedId ? "Ask a question…" : "Select a notebook first"}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={busy || !selectedId}
          aria-label="Question input"
        />
        <button
          type="submit"
          className="nc-send-btn"
          disabled={busy || !input.trim() || !selectedId}
          aria-label="Send question"
        >
          {busy ? (
            <SpinnerIcon />
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          )}
        </button>
      </form>

      <p className="nc-disclaimer">
        Powered by NotebookLM · Responses may not reflect live sensor data
      </p>
    </div>
  );
}
